"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import { Info, ExternalLink } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface FormulaEditorProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  showLanguageInfo?: boolean;
}

// Token types for syntax highlighting
type TokenType =
  | "comment"
  | "function"
  | "string"
  | "number"
  | "operator"
  | "keyword"
  | "variable-def"
  | "punctuation"
  | "text";

interface Token {
  type: TokenType;
  value: string;
}

// Tokenize formula code
function tokenize(code: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  const functions = [
    "lookup",
    "if_else",
    "round",
    "ceil",
    "floor",
    "min",
    "max",
    "sum",
    "avg",
    "abs",
    "sqrt",
    "pow",
    "len",
    "str",
    "int",
    "float",
    "bool",
    "count",
    "coalesce",
  ];

  const keywords = ["and", "or", "not", "true", "false", "True", "False", "None"];

  while (i < code.length) {
    // Comments
    if (code[i] === "#") {
      let comment = "";
      while (i < code.length && code[i] !== "\n") {
        comment += code[i];
        i++;
      }
      tokens.push({ type: "comment", value: comment });
      continue;
    }

    // Newlines - preserve them
    if (code[i] === "\n") {
      tokens.push({ type: "text", value: "\n" });
      i++;
      continue;
    }

    // Whitespace
    if (/\s/.test(code[i])) {
      let ws = "";
      while (i < code.length && /\s/.test(code[i]) && code[i] !== "\n") {
        ws += code[i];
        i++;
      }
      tokens.push({ type: "text", value: ws });
      continue;
    }

    // Strings (double quotes)
    if (code[i] === '"') {
      let str = '"';
      i++;
      while (i < code.length && code[i] !== '"' && code[i] !== "\n") {
        if (code[i] === "\\") {
          str += code[i];
          i++;
          if (i < code.length) {
            str += code[i];
            i++;
          }
        } else {
          str += code[i];
          i++;
        }
      }
      if (i < code.length && code[i] === '"') {
        str += '"';
        i++;
      }
      tokens.push({ type: "string", value: str });
      continue;
    }

    // Strings (single quotes)
    if (code[i] === "'") {
      let str = "'";
      i++;
      while (i < code.length && code[i] !== "'" && code[i] !== "\n") {
        if (code[i] === "\\") {
          str += code[i];
          i++;
          if (i < code.length) {
            str += code[i];
            i++;
          }
        } else {
          str += code[i];
          i++;
        }
      }
      if (i < code.length && code[i] === "'") {
        str += "'";
        i++;
      }
      tokens.push({ type: "string", value: str });
      continue;
    }

    // Numbers
    if (/[0-9]/.test(code[i]) || (code[i] === "." && /[0-9]/.test(code[i + 1] || ""))) {
      let num = "";
      while (i < code.length && /[0-9.]/.test(code[i])) {
        num += code[i];
        i++;
      }
      tokens.push({ type: "number", value: num });
      continue;
    }

    // Multi-char operators
    if ([">=", "<=", "==", "!=", "**"].includes(code.slice(i, i + 2))) {
      tokens.push({ type: "operator", value: code.slice(i, i + 2) });
      i += 2;
      continue;
    }

    // Single-char operators
    if (["+", "-", "*", "/", ">", "<", "="].includes(code[i])) {
      // Check for variable definition (identifier followed by =)
      if (code[i] === "=" && tokens.length > 0) {
        const lastToken = tokens[tokens.length - 1];
        if (lastToken.type === "text" && /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(lastToken.value.trim())) {
          // Convert the previous identifier to variable-def
          const trimmed = lastToken.value.trim();
          const ws = lastToken.value.slice(0, lastToken.value.indexOf(trimmed));
          tokens.pop();
          if (ws) tokens.push({ type: "text", value: ws });
          tokens.push({ type: "variable-def", value: trimmed });
          tokens.push({ type: "operator", value: "=" });
          i++;
          continue;
        }
      }
      tokens.push({ type: "operator", value: code[i] });
      i++;
      continue;
    }

    // Punctuation
    if (["(", ")", ",", "[", "]", ":"].includes(code[i])) {
      tokens.push({ type: "punctuation", value: code[i] });
      i++;
      continue;
    }

    // Identifiers (variables, functions, keywords)
    if (/[a-zA-Z_]/.test(code[i])) {
      let ident = "";
      while (i < code.length && /[a-zA-Z0-9_]/.test(code[i])) {
        ident += code[i];
        i++;
      }

      // Check if it's followed by ( making it a function
      let lookAhead = i;
      while (lookAhead < code.length && /\s/.test(code[lookAhead])) {
        lookAhead++;
      }
      const isFunction = code[lookAhead] === "(" && functions.includes(ident);

      if (isFunction) {
        tokens.push({ type: "function", value: ident });
      } else if (keywords.includes(ident)) {
        tokens.push({ type: "keyword", value: ident });
      } else {
        tokens.push({ type: "text", value: ident });
      }
      continue;
    }

    // Anything else
    tokens.push({ type: "text", value: code[i] });
    i++;
  }

  return tokens;
}

// Convert tokens to highlighted HTML - Palette Logissimo
function highlightCode(code: string): React.ReactNode[] {
  const tokens = tokenize(code);
  
  return tokens.map((token, index) => {
    const colorClasses: Record<TokenType, string> = {
      comment: "text-laposte-teal",          // #00B2A9
      function: "text-laposte-yellow",        // #FFCC00
      string: "text-laposte-magenta",         // #E6007E
      number: "text-laposte-blue",            // #0066B3
      operator: "text-laposte-teal-dark",     // #009990
      keyword: "text-laposte-magenta-dark",   // #CC006E
      "variable-def": "text-laposte-yellow-dark", // #E6B800
      punctuation: "text-zinc-400",
      text: "text-zinc-100",
    };

    return (
      <span key={index} className={colorClasses[token.type]}>
        {token.value}
      </span>
    );
  });
}

const FormulaEditor = React.forwardRef<HTMLTextAreaElement, FormulaEditorProps>(
  ({ className, value, onChange, showLanguageInfo = true, rows = 5, ...props }, ref) => {
    const [scrollTop, setScrollTop] = React.useState(0);
    const [scrollLeft, setScrollLeft] = React.useState(0);
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const highlightRef = React.useRef<HTMLPreElement>(null);

    // Sync refs
    React.useImperativeHandle(ref, () => textareaRef.current!);

    const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
      const target = e.target as HTMLTextAreaElement;
      setScrollTop(target.scrollTop);
      setScrollLeft(target.scrollLeft);
    };

    // Sync scroll position of the highlight layer
    React.useEffect(() => {
      if (highlightRef.current) {
        highlightRef.current.scrollTop = scrollTop;
        highlightRef.current.scrollLeft = scrollLeft;
      }
    }, [scrollTop, scrollLeft]);

    return (
      <div className="relative">
        {showLanguageInfo && (
          <div className="absolute top-2 right-2 z-20">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-2 py-1 text-xs rounded-md bg-zinc-800/80 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-700/80 transition-colors border border-zinc-700"
                >
                  <Info className="h-3 w-3" />
                  <span>Formula DSL</span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs p-3 space-y-2">
                <div className="font-semibold text-sm">Langage Formula DSL</div>
                <p className="text-xs text-muted-foreground">
                  Un langage de formules personnalisé pour définir des calculs 
                  avec lookups, conditions et opérations arithmétiques.
                </p>
                <div className="pt-1 border-t border-border">
                  <a
                    href="/docs/formula-dsl"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Voir la documentation
                  </a>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        <div className="relative font-mono text-sm">
          {/* Syntax highlighted background layer */}
          <pre
            ref={highlightRef}
            aria-hidden="true"
            className={cn(
              "absolute inset-0 overflow-hidden pointer-events-none",
              "whitespace-pre-wrap break-words",
              "p-3 rounded-md border border-transparent",
              "bg-zinc-900 text-zinc-100",
              className
            )}
            style={{
              fontFamily: "inherit",
              fontSize: "inherit",
              lineHeight: "1.5",
              minHeight: rows ? `${rows * 1.5}em` : undefined,
            }}
          >
            <code>{highlightCode(value || "")}</code>
            {/* Invisible character to ensure the pre has content even when empty */}
            <br />
          </pre>

          {/* Transparent textarea for input */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onScroll={handleScroll}
            rows={rows}
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            className={cn(
              "relative w-full resize-none",
              "whitespace-pre-wrap break-words",
              "p-3 rounded-md border border-zinc-700",
              "bg-transparent text-transparent caret-white",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background",
              "placeholder:text-zinc-500",
              className
            )}
            style={{
              fontFamily: "inherit",
              fontSize: "inherit",
              lineHeight: "1.5",
              caretColor: "white",
              WebkitTextFillColor: "transparent",
            }}
            {...props}
          />
        </div>

        {/* Légende des couleurs - Palette Logissimo */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-laposte-teal" />
            <span className="text-muted-foreground">Commentaires</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-laposte-yellow" />
            <span className="text-muted-foreground">Fonctions</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-laposte-magenta" />
            <span className="text-muted-foreground">Chaînes</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-laposte-blue" />
            <span className="text-muted-foreground">Nombres</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-laposte-yellow-dark" />
            <span className="text-muted-foreground">Variables</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-laposte-magenta-dark" />
            <span className="text-muted-foreground">Mots-clés</span>
          </span>
        </div>
      </div>
    );
  }
);

FormulaEditor.displayName = "FormulaEditor";

// Read-only highlighted formula display component
interface HighlightedFormulaProps {
  code: string;
  className?: string;
}

function HighlightedFormula({ code, className }: HighlightedFormulaProps) {
  return (
    <pre
      className={cn(
        "font-mono text-sm whitespace-pre-wrap break-words",
        "p-3 rounded-lg bg-zinc-900 text-zinc-100",
        className
      )}
    >
      <code>{highlightCode(code || "")}</code>
    </pre>
  );
}

HighlightedFormula.displayName = "HighlightedFormula";

export { FormulaEditor, HighlightedFormula };

